import { BasePageDto } from "@/types/dto/base-page-dto";
import { BaseResponse } from "@/types/dto/base-response";
import { NoticeDetailDto } from "@/types/dto/notice-detail-dto";
import { NoticeDto } from "@/types/dto/notice-dto";
import { CreateNoticePayload } from "@/types/payload/create-notice-payload";
import { IdPayload } from "@/types/payload/id-payload";
import { IdsPayload } from "@/types/payload/ids-payload";
import { NoticePagePayload } from "@/types/payload/notice-page-payload";
import { UpdateNoticePayload } from "@/types/payload/update-notice-payload";
import { AxiosInstance } from "axios";
import { baseApi } from "./base-api";

enum NoticeServiceEndpoint {
  Create = "/b2b-notice",
  GetById = "/b2b-notice",
  GetPage = "/b2b-notices",
  Update = "/b2b-notice",
  Delete = "/b2b-notice",
  DeleteBulk = "/b2b-notice/bulk",
}

class NoticeService {
  private static instance: NoticeService;
  private _api: AxiosInstance;

  private constructor(api: AxiosInstance) {
    this._api = api;
  }

  static getInstance(apiClient = baseApi): NoticeService {
    if (!NoticeService.instance) {
      NoticeService.instance = new NoticeService(apiClient);
    }
    return NoticeService.instance;
  }

  async create(
    payload: CreateNoticePayload
  ): Promise<BaseResponse<NoticeDto, CreateNoticePayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<NoticeDto, CreateNoticePayload>
      >(NoticeServiceEndpoint.Create, {
        title: payload.title,
        content: payload.content,
        popupStartDate: payload.popupStartDate,
        popupEndDate: payload.popupEndDate,
      });

      return response.data;
    } catch (error) {
      console.error("Error create:", error);
      throw error;
    }
  }

  async getById(
    payload: IdPayload
  ): Promise<BaseResponse<NoticeDetailDto, CreateNoticePayload>> {
    try {
      const response = await this._api.get<
        BaseResponse<NoticeDetailDto, CreateNoticePayload>
      >(`${NoticeServiceEndpoint.GetById}/${payload.id}`);

      return response.data;
    } catch (error) {
      console.error("Error getById:", error);
      throw error;
    }
  }

  async getPage(
    payload: NoticePagePayload
  ): Promise<BaseResponse<BasePageDto<NoticeDetailDto>, NoticePagePayload>> {
    try {
      const queryParams = new URLSearchParams({
        page: payload.page.toString(),
        size: payload.size.toString(),
      });

      if (payload.isFiltered !== undefined) {
        queryParams.append("isFiltered", payload.isFiltered.toString());
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<NoticeDetailDto>, NoticePagePayload>
      >(`${NoticeServiceEndpoint.GetPage}?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      console.error("Error getPage:", error);
      throw error;
    }
  }

  async update(
    id: number,
    payload: UpdateNoticePayload
  ): Promise<BaseResponse<NoticeDto, UpdateNoticePayload>> {
    try {
      const response = await this._api.patch<
        BaseResponse<NoticeDto, UpdateNoticePayload>
      >(`${NoticeServiceEndpoint.Update}/${id}`, {
        title: payload.title,
        content: payload.content,
        popupStartDate: payload.popupStartDate,
        popupEndDate: payload.popupEndDate,
      });

      return response.data;
    } catch (error) {
      console.error("Error update:", error);
      throw error;
    }
  }

  async delete(payload: IdPayload): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.delete<BaseResponse<void, void>>(
        `${NoticeServiceEndpoint.Delete}/${payload.id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error delete:", error);
      throw error;
    }
  }

  async deleteBulk(payload: IdsPayload): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.delete<BaseResponse<void, void>>(
        `${NoticeServiceEndpoint.DeleteBulk}`,
        {
          data: payload,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error delete:", error);
      throw error;
    }
  }
}

export const noticeService = NoticeService.getInstance(baseApi);
